import axios, { type AxiosResponse, type AxiosRequestConfig } from "axios";

let accessToken: string | null = null;
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return (
    accessToken ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("platformToken")
  );
}

export function setRefreshToken(token: string | null) {
  if (token) {
    localStorage.setItem("refreshToken", token);
  } else {
    localStorage.removeItem("refreshToken");
  }
}

export function getRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

export function setTenantSubdomain(subdomain: string | null) {
  if (subdomain) {
    localStorage.setItem("tenantSubdomain", subdomain);
  } else {
    localStorage.removeItem("tenantSubdomain");
  }
}

export function getTenantSubdomain(): string | null {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname.toLowerCase();
  const parts = host.split(".");
  if (host === "localhost" || host === "127.0.0.1") {
    return localStorage.getItem("tenantSubdomain") || null;
  }
  if (host.endsWith(".localhost")) {
    return parts[0];
  }
  if (parts.length >= 3 && !["www", "app", "api", "staging", "erp", "admin"].includes(parts[0])) {
    return parts[0];
  }
  return localStorage.getItem("tenantSubdomain") || null;
}

export function clearAuthSession() {
  setAccessToken(null);
  setRefreshToken(null);
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userProfile");
  localStorage.removeItem("tenantSubdomain");
  localStorage.removeItem("tenantId");
  localStorage.removeItem("userRole");
  localStorage.removeItem("platformToken");
  if (typeof window !== "undefined") {
    sessionStorage.clear();
  }
}

const api = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string) || "",
});

// Request interceptor: attach Authorization header and tenant headers
api.interceptors.request.use(async (config) => {
  const currentBase = config.baseURL || (import.meta.env.VITE_API_BASE_URL as string) || "";
  if (currentBase.endsWith("/api") && config.url?.startsWith("/api/")) {
    config.url = config.url.substring(4);
  }

  const effectiveToken =
    accessToken ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("platformToken");

  if (effectiveToken) {
    config.headers.Authorization = `Bearer ${effectiveToken}`;
  }

  const tenantSubdomain = getTenantSubdomain();
  if (tenantSubdomain) {
    config.headers["X-Subdomain"] = tenantSubdomain;
    config.headers["X-Tenant-ID"] = tenantSubdomain;
  }

  // Prevent browser from serving stale API responses from disk/memory cache
  if (config.method?.toLowerCase() === "get") {
    config.headers["Cache-Control"] = "no-cache";
    config.headers["Pragma"] = "no-cache";
  }

  return config;
});

// Response interceptor: handle automatic silent token refreshing on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Avoid infinite loop if auth requests fail with 401
    const isAuthUrl =
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/session");

    if (error.response?.status === 401 && !isAuthUrl && !originalRequest._retry) {
      originalRequest._retry = true;
      const rToken = getRefreshToken();

      if (!rToken) {
        clearAuthSession();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || "";
        const res = await axios.post<{ accessToken: string; refreshToken: string }>(
          `${baseUrl}/api/auth/refresh`,
          { refreshToken: rToken }
        );

        const newAccessToken = res.data.accessToken;
        const newRefreshToken = res.data.refreshToken;

        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);

        isRefreshing = false;
        refreshSubscribers.forEach((callback) => callback(newAccessToken));
        refreshSubscribers = [];

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        clearAuthSession();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export interface ApiResponseEnvelope<T> {
  success?: boolean;
  data: T;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    [key: string]: any;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * Type-safe API unwrapping functions that return the core `data` payload
 */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponseEnvelope<T> | T> = await api.get(url, config);
  if (response.data && typeof response.data === "object" && "data" in response.data) {
    return (response.data as ApiResponseEnvelope<T>).data;
  }
  return response.data as T;
}

export async function apiPost<T>(url: string, body?: any, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponseEnvelope<T> | T> = await api.post(url, body, config);
  if (response.data && typeof response.data === "object" && "data" in response.data) {
    return (response.data as ApiResponseEnvelope<T>).data;
  }
  return response.data as T;
}

export async function apiPut<T>(url: string, body?: any, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponseEnvelope<T> | T> = await api.put(url, body, config);
  if (response.data && typeof response.data === "object" && "data" in response.data) {
    return (response.data as ApiResponseEnvelope<T>).data;
  }
  return response.data as T;
}

export async function apiPatch<T>(url: string, body?: any, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponseEnvelope<T> | T> = await api.patch(url, body, config);
  if (response.data && typeof response.data === "object" && "data" in response.data) {
    return (response.data as ApiResponseEnvelope<T>).data;
  }
  return response.data as T;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<ApiResponseEnvelope<T> | T> = await api.delete(url, config);
  if (response.data && typeof response.data === "object" && "data" in response.data) {
    return (response.data as ApiResponseEnvelope<T>).data;
  }
  return response.data as T;
}

export async function apiRequest<T>(config: AxiosRequestConfig): Promise<T> {
  const response: AxiosResponse<T> = await api.request<T>(config);
  return response.data;
}

export default api;
